import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useBillabilityReport } from '../../hooks/useReports';
import ReportFilters from '../../components/domain/ReportFilters';
import { Spinner } from '../../components/common/Spinner';
import { UsernameTrendPopover } from '../../components/reports/UsernameTrendPopover';
import { useFlag } from '../../services/flagsContext';
import type { BillabilityRow, BillabilityReport } from '../../services/api/reports';
import { formatHours } from '../../utils/format';
import { MONEY_GLOSSARY } from '../../utils/moneyGlossary';
import { LEGACY_EN_NUMBER_LOCALE, formatNumber } from '../../utils/localeFormat';

/**
 * Phase 57 — Billability Report
 *
 * View-only per-user-per-month billability report. 13-column table covering
 * billable / non-billable hours (split into Internal / PTO / Other), cost,
 * billable value + invoiced amount (side-by-side per L-03), and two margin
 * pairs (production margin vs. cash margin) per D-06.
 *
 * Margin% cells render the em-dash `—` when the underlying value is `null`
 * (revenue was zero — D-06 frontend half). Negative numeric margin cells are
 * highlighted in red. A footnote at the bottom of the table explains the
 * idle-user-zero-cost behavior (D-05a).
 *
 * No XLSX export in v1 (D-07 — deferred). No user filter (CONTEXT.md doesn't
 * request it for v1).
 */

function formatMoney(value: number): string {
  // CZK whole-koruna display — matches existing report convention.
  return value.toFixed(0);
}

function formatMarginPct(value: number | null): string {
  // D-06 frontend half: render em-dash when null (revenue was zero).
  return value === null ? '—' : `${(value * 100).toFixed(1)}%`;
}

/**
 * G-05 (Phase 57 Plan 09) — work-delivered cell formatter.
 *
 * Locked decisions from 57-VERIFICATION.md G-05 `locked_decisions.display_format`
 * + `idle_user_render`:
 *   - Zero-commit users render `—` em-dash (distinct from N-commit-zero-LOC
 *     literal `N commits · +0/−0` — zero-commit case takes precedence).
 *   - Non-zero commit users render `{N} commits · +{A}/−{D}` with en-US locale
 *     thousands separator (`1,234` not `1234`) and U+2212 minus character
 *     (`−`, NOT the ASCII hyphen-minus `-` which would visually collide with
 *     the `/` separator at small font sizes).
 */
function formatWorkDelivered(wd: {
  commitCount: number;
  additions: number;
  deletions: number;
}): string {
  if (wd.commitCount === 0) return '—';
  return `${wd.commitCount} commits · +${formatNumber(wd.additions, undefined, LEGACY_EN_NUMBER_LOCALE)}/−${formatNumber(wd.deletions, undefined, LEGACY_EN_NUMBER_LOCALE)}`;
}

/**
 * D-06 — Negative-margin highlighting convention.
 *
 * Negative numeric margin cells (`margin1` / `margin2`) render in
 * `text-red-600 font-medium` so admins immediately see underwater users. The
 * same convention applies to the matching margin% cell when the percentage is
 * non-null. When the percentage is null (revenue was zero — em-dash render
 * branch), the cell renders in muted `text-gray-500` because the em-dash
 * itself communicates "no signal."
 *
 * Both call sites in the body / footer inline the literal `text-red-600`
 * decision so a `grep text-red-600` audit of this file shows the styling at
 * the actual JSX decision points, not buried in a helper. The helper below is
 * only for the margin% cell where the null-branch adds a third color.
 *
 * Existing report convention (per PATTERNS.md §Negative-margin highlighting):
 *   negative numeric    → "text-red-600 font-medium"
 *   non-negative numeric → "text-gray-900"
 *   null margin%        → "text-gray-500"
 */
function marginPctCellClass(marginValue: number, marginPctValue: number | null): string {
  if (marginPctValue === null) return 'text-gray-500';
  return marginValue < 0 ? 'text-red-600 font-medium' : 'text-gray-900';
}

/**
 * G-02 — Click-header column sort (asc → desc → off cycle) over already-loaded
 * `data.rows`. NULLS LAST for null margin%. URL-synced via `sort` + `dir` query
 * params alongside the existing `from`/`to`. Sort is purely client-side; the
 * `useBillabilityReport` queryKey does NOT include sort state so a sort click
 * never triggers a refetch.
 */
type SortKey =
  | 'userName'
  | 'billableHours'
  | 'internalHours'
  | 'ptoHours'
  | 'otherHours'
  | 'totalNonBill'
  | 'totalHours'
  | 'cost'
  | 'billableValue'
  | 'invoicedAmount'
  | 'paidAmount'
  | 'margin1'
  | 'marginPct1'
  | 'margin2'
  | 'marginPct2'
  | 'workDelivered.commitCount';

const SORT_KEYS: readonly SortKey[] = [
  'userName',
  'billableHours',
  'internalHours',
  'ptoHours',
  'otherHours',
  'totalNonBill',
  'totalHours',
  'cost',
  'billableValue',
  'invoicedAmount',
  'paidAmount',
  'margin1',
  'marginPct1',
  'margin2',
  'marginPct2',
  'workDelivered.commitCount',
];

type SortState = { sort: SortKey | null; dir: 'asc' | 'desc' | null };

/**
 * Pure compare function used by `Array.prototype.sort`. NULLS LAST applies to
 * `marginPct1` / `marginPct2` (idle users render `—`, and should also fall to
 * the bottom of the list under both directions).
 */
function compareRows(
  a: BillabilityRow,
  b: BillabilityRow,
  sortKey: SortKey,
  dir: 'asc' | 'desc'
): number {
  const sign = dir === 'desc' ? -1 : 1;

  if (sortKey === 'userName') {
    return a.userName.localeCompare(b.userName) * sign;
  }
  if (sortKey === 'totalNonBill') {
    const aT = a.internalHours + a.ptoHours + a.otherHours;
    const bT = b.internalHours + b.ptoHours + b.otherHours;
    return (aT - bT) * sign;
  }
  if (sortKey === 'marginPct1' || sortKey === 'marginPct2') {
    // NULLS LAST regardless of direction.
    const av = a[sortKey];
    const bv = b[sortKey];
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return (av - bv) * sign;
  }
  if (sortKey === 'workDelivered.commitCount') {
    // G-05 — NULLS LAST for zero-commit users (mirrors marginPct1/marginPct2
    // pattern; an idle user has no engineering output to rank by, so they
    // fall to the bottom under both directions). Primary sort = commitCount;
    // tie-break = LOC churn (additions + deletions) per locked decision in
    // 57-VERIFICATION.md G-05 `locked_decisions.sort_key`.
    const aZero = a.workDelivered.commitCount === 0;
    const bZero = b.workDelivered.commitCount === 0;
    if (aZero && bZero) return 0;
    if (aZero) return 1;
    if (bZero) return -1;
    const countDiff = (a.workDelivered.commitCount - b.workDelivered.commitCount) * sign;
    if (countDiff !== 0) return countDiff;
    const aChurn = a.workDelivered.additions + a.workDelivered.deletions;
    const bChurn = b.workDelivered.additions + b.workDelivered.deletions;
    return (aChurn - bChurn) * sign;
  }
  // All remaining keys are plain number fields on BillabilityRow. `?? 0`
  // covers `paidAmount`, which is optional until the backend carrying it is
  // deployed — a missing Paid sorts as 0, which is what its cell renders.
  const av = a[sortKey] ?? 0;
  const bv = b[sortKey] ?? 0;
  return (av - bv) * sign;
}

function parseSortFromUrl(searchParams: URLSearchParams): SortState {
  const rawSort = searchParams.get('sort');
  const rawDir = searchParams.get('dir');
  if (!rawSort || !rawDir) return { sort: null, dir: null };
  if (rawDir !== 'asc' && rawDir !== 'desc') return { sort: null, dir: null };
  if (!(SORT_KEYS as readonly string[]).includes(rawSort)) return { sort: null, dir: null };
  return { sort: rawSort as SortKey, dir: rawDir };
}

/**
 * MKS-331 — the row filters. All three live in the URL (`q`, `etype`, `zeros`) so a filtered
 * report is a shareable link, like the date range and the sort already are.
 */
export interface BillabilityRowFilters {
  /** Free text matched against the user name and the email, case-insensitive. */
  query: string;
  /** An exact `employeeTypeName`, `NO_EMPLOYEE_TYPE` for the no-category people, or '' for all. */
  employeeType: string;
  /** Off by default: a period's zero-hour rows are noise, not information. */
  showZeroHours: boolean;
}

/** The employee-type option standing for "no employee type set". */
export const NO_EMPLOYEE_TYPE = '__none__';

export const EMPTY_ROW_FILTERS: BillabilityRowFilters = {
  query: '',
  employeeType: '',
  showZeroHours: false,
};

/** Pure. Returns the rows to render, in the order given. */
export function filterBillabilityRows(
  rows: BillabilityRow[],
  filters: BillabilityRowFilters
): BillabilityRow[] {
  const needle = filters.query.trim().toLowerCase();
  return rows.filter((row) => {
    // A person with no hours at all in the period has nothing to read; the toggle brings
    // them back. `totalHours` already covers billable + internal + PTO + other.
    if (!filters.showZeroHours && row.totalHours === 0) return false;
    if (filters.employeeType) {
      const type = row.employeeTypeName ?? null;
      if (filters.employeeType === NO_EMPLOYEE_TYPE) {
        if (type !== null) return false;
      } else if (type !== filters.employeeType) {
        return false;
      }
    }
    if (needle) {
      const haystack = `${row.userName} ${row.email}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
}

/** The employee-type options present in this report, sorted, plus the no-category bucket. */
export function employeeTypeOptions(rows: BillabilityRow[]): string[] {
  const names = new Set<string>();
  let anyWithout = false;
  for (const row of rows) {
    if (row.employeeTypeName) names.add(row.employeeTypeName);
    else anyWithout = true;
  }
  const sorted = [...names].sort((a, b) => a.localeCompare(b));
  return anyWithout ? [...sorted, NO_EMPLOYEE_TYPE] : sorted;
}

/**
 * MKS-331 — totals over the rows actually on screen.
 *
 * With filters in play the server's period-wide totals would disagree with the rows above
 * them, which reads as a bug. `base` carries the fields that are not a row roll-up
 * (`invoicedDiscountAmount` is about the period's invoices, not about who is listed).
 */
export function sumBillabilityTotals(
  rows: BillabilityRow[],
  base: BillabilityReport['totals']
): BillabilityReport['totals'] {
  const sum = (pick: (row: BillabilityRow) => number) =>
    rows.reduce((acc, row) => acc + pick(row), 0);

  const billableValue = sum((r) => r.billableValue);
  const invoicedAmount = sum((r) => r.invoicedAmount);
  const cost = sum((r) => r.cost);
  const margin1 = billableValue - cost;
  const margin2 = invoicedAmount - cost;

  return {
    ...base,
    billableHours: sum((r) => r.billableHours),
    internalHours: sum((r) => r.internalHours),
    ptoHours: sum((r) => r.ptoHours),
    otherHours: sum((r) => r.otherHours),
    totalHours: sum((r) => r.totalHours),
    billableValue,
    invoicedAmount,
    // MKS-332 follow-up — rolls up like every other per-row money figure.
    paidAmount: sum((r) => r.paidAmount ?? 0),
    cost,
    userCosts: sum((r) => r.userCosts),
    extraCosts: sum((r) => r.extraCosts),
    assignmentCosts: sum((r) => r.assignmentCosts),
    margin1,
    // Same divide-by-zero rule as the backend: null, which renders as the em-dash.
    marginPct1: billableValue === 0 ? null : margin1 / billableValue,
    margin2,
    marginPct2: invoicedAmount === 0 ? null : margin2 / invoicedAmount,
    workDelivered: {
      commitCount: sum((r) => r.workDelivered.commitCount),
      additions: sum((r) => r.workDelivered.additions),
      deletions: sum((r) => r.workDelivered.deletions),
    },
  };
}

function nextSortState(current: SortState, clickedKey: SortKey): SortState {
  if (current.sort !== clickedKey) return { sort: clickedKey, dir: 'asc' };
  if (current.dir === 'asc') return { sort: clickedKey, dir: 'desc' };
  // current.dir === 'desc' (or null defensive fallback) → clear.
  return { sort: null, dir: null };
}

/**
 * G-02 — The 13 column headers in render order. Each entry binds a SortKey to
 * the displayed label and the alignment of the cell. The header label is the
 * post-G-01 explicit text (the renamed margin labels from Plan 57-05), so the click-to-sort UI
 * uses the same string the user sees and the URL ?sort=marginPct1 maps to
 * "Margin % (Billable)" unambiguously.
 *
 * Declared at module scope (rather than inside BillabilityTable) so it is a
 * stable reference across renders and so the array literal is created once.
 */
interface SortableHeader {
  key: SortKey;
  label: string;
  align: 'left' | 'right';
  /** MKS-332 — the definition, shown as the column's hover title. */
  tooltip?: string;
}

const SORTABLE_HEADERS: ReadonlyArray<SortableHeader> = [
  { key: 'userName', label: 'User', align: 'left' },
  { key: 'billableHours', label: 'Billable hrs', align: 'right' },
  { key: 'internalHours', label: 'Internal hrs', align: 'right' },
  { key: 'ptoHours', label: 'PTO hrs', align: 'right' },
  { key: 'otherHours', label: 'Other hrs', align: 'right' },
  { key: 'totalNonBill', label: 'Total non-bill', align: 'right' },
  { key: 'totalHours', label: 'Total hrs', align: 'right' },
  { key: 'cost', label: 'Cost (CZK)', align: 'right' },
  { key: 'billableValue', label: 'Consumption (drawdown)', align: 'right' },
  { key: 'invoicedAmount', label: 'Invoiced amt', align: 'right' },
  // MKS-332 follow-up — flag-on only; `applyMoneyVocabulary` drops it when
  // the flag is off, where this column has never existed.
  { key: 'paidAmount', label: 'Paid', align: 'right' },
  { key: 'margin1', label: 'Margin (Billable)', align: 'right' },
  { key: 'marginPct1', label: 'Margin % (Billable)', align: 'right' },
  { key: 'margin2', label: 'Margin (Invoiced)', align: 'right' },
  { key: 'marginPct2', label: 'Margin % (Invoiced)', align: 'right' },
  { key: 'workDelivered.commitCount', label: 'Work Delivered', align: 'right' },
];

/**
 * MKS-332 — the Billability Report's money columns, named and explained.
 *
 * Petra's staging review (2026-10-16, item 1) settled a question the first
 * pass left open. That pass kept "Drawdown (per person)" and two margins on
 * the grounds that ADR 0002 Decision D calls the per-user figure `drawdown`,
 * not revenue — a retainer's paušál is earned by the project, not by one
 * person. The reasoning is sound and the decision was still wrong for a user:
 * it left one screen speaking a vocabulary no other screen speaks, which is
 * precisely what this ticket exists to end. Petra: "Move it to Earned ·
 * Invoiced · Paid · Cost · Margin with the shared definitions and tooltips."
 *
 * So the LABELS are now the shared five, and every tooltip is the shared
 * definition. What the old labels were protecting does not disappear — it
 * moves into the tooltip, as the sentence that says this is the per-person
 * share and must not be summed against a project's Earned. A reader who
 * hovers learns the caveat; a reader who glances is no longer told that this
 * page measures something with a name they have never seen.
 *
 * One Margin, not two: (Earned − Cost) ÷ Earned, the formula every other
 * surface uses. The second pair (vs invoiced) is a different denominator and
 * therefore a second definition, so it leaves the flag-on view entirely —
 * `margin2` / `marginPct2` are still in the API for anyone reconciling.
 */
const MKS332_HEADER_OVERRIDES: Partial<Record<SortKey, { label: string; tooltip: string }>> = {
  cost: {
    label: MONEY_GLOSSARY.cost.label,
    tooltip: MONEY_GLOSSARY.cost.definition,
  },
  billableValue: {
    label: MONEY_GLOSSARY.earned.label,
    tooltip: `${MONEY_GLOSSARY.earned.definition} On this report it is the PER-PERSON share — this person's hours × their resolved external rate — so it must not be summed against a project's Earned: a retainer's paušál is earned by the project, not by one person.`,
  },
  invoicedAmount: {
    label: MONEY_GLOSSARY.invoiced.label,
    tooltip: `${MONEY_GLOSSARY.invoiced.definition} Here it is apportioned to the person whose entries the invoice covers.`,
  },
  paidAmount: {
    label: MONEY_GLOSSARY.paid.label,
    tooltip: `${MONEY_GLOSSARY.paid.definition} Apportioned to a person by exactly the weights Invoiced uses.`,
  },
  margin1: {
    label: 'Margin (CZK)',
    tooltip: 'Earned − Cost, in CZK.',
  },
  marginPct1: {
    label: MONEY_GLOSSARY.margin.label,
    tooltip: MONEY_GLOSSARY.margin.definition,
  },
};

/**
 * The money column set.
 *
 * This both RENAMES and SELECTS, because the vocabulary change is not only a
 * change of words: Paid did not exist before, and the second margin pair
 * cannot survive a ticket whose point is that there is one margin.
 *
 * The table body and footer render fixed `<td>`s rather than mapping over
 * these headers, so the same column set is restated there. Change one, change
 * all three, or the header row stops lining up with its columns.
 */
function applyMoneyVocabulary(
  headers: ReadonlyArray<SortableHeader>,
): ReadonlyArray<SortableHeader> {
  return headers
    .filter((header) => header.key !== 'margin2' && header.key !== 'marginPct2')
    .map((header) => {
      const override = MKS332_HEADER_OVERRIDES[header.key];
      return override ? { ...header, ...override } : header;
    });
}

/**
 * Quick task 260602-g6r — Hover-trendline popover support.
 *
 * The username cell wraps `row.userName` in a hover/focus-aware <span> that
 * notifies the parent page via `onUserNameHoverEnter` / `onUserNameHoverLeave`
 * / `onUserNameFocus` / `onUserNameBlur`. The parent owns the open-delay
 * timer (250ms hover → open; 150ms leave → close; immediate on focus/blur)
 * and renders a single shared <UsernameTrendPopover> outside the table.
 */
/**
 * MKS-331 — the four columns that move out of the main grid when the report is compact.
 *
 * Sixteen columns do not fit in 1600 px, and the ones a reader scans for — who, billable
 * hours, cost, revenue, margin — were the ones pushed off the right edge. The hour
 * breakdown (and the cost breakdown, which had no column at all) moves into a per-person
 * detail row, so nothing is lost and nothing needs horizontal scrolling to be read.
 */
const DETAIL_SORT_KEYS: ReadonlySet<SortKey> = new Set<SortKey>([
  'internalHours',
  'ptoHours',
  'otherHours',
  'totalNonBill',
]);

function BillabilityTable({
  rows,
  totals,
  sortState,
  onHeaderClick,
  onUserNameHoverEnter,
  onUserNameHoverLeave,
  onUserNameFocus,
  onUserNameBlur,
}: {
  rows: BillabilityRow[];
  totals: BillabilityReport['totals'];
  sortState: SortState;
  onHeaderClick: (key: SortKey) => void;
  onUserNameHoverEnter?: (userId: number, userName: string, rect: ReturnType<HTMLElement['getBoundingClientRect']>) => void;
  onUserNameHoverLeave?: () => void;
  onUserNameFocus?: (userId: number, userName: string, rect: ReturnType<HTMLElement['getBoundingClientRect']>) => void;
  onUserNameBlur?: () => void;
}) {
  // MKS-479 — the report lists people archived since the period, so the name
  // cell says which of them that is. Flag off, no badge renders at all.
  const archivedUsersOn = useFlag('mks_479_billability_archived_users');

  // Which people have their breakdown open. Local to the table: it is a display detail, not
  // something worth putting in the URL next to the filters.
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set<number>());
  const toggleExpanded = useCallback((userId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(userId)) next.add(userId);
      return next;
    });
  }, []);

  const headers = applyMoneyVocabulary(
    SORTABLE_HEADERS.filter((h) => !DETAIL_SORT_KEYS.has(h.key)),
  );
  // The sticky user column needs its own background, or the scrolled-under cells show
  // through it. Each of head / body / foot keeps the background its own row already has.
  const stickyHead = 'sticky left-0 z-20 bg-gray-50';
  const stickyBody = 'sticky left-0 z-10 bg-white group-hover:bg-gray-50';
  const stickyFoot = 'sticky left-0 z-10 bg-blue-50';

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500">
        No data available for the selected filters.
      </div>
    );
  }

  // D-06 also applies to the totals row — if the roll-up is negative, it reads red.
  const tMargin1NumClass = totals.margin1 < 0 ? 'text-red-600 font-medium' : 'text-gray-900';
  const tMargin1PctClass = marginPctCellClass(totals.margin1, totals.marginPct1);

  // G-02 — sort source of truth:
  //   - If `sortState.sort` is set, use the comparator with the user-selected
  //     column + direction (NULLS LAST for marginPct1/marginPct2).
  //   - Otherwise fall back to the default userName ascending — mirrors Payroll
  //     Report (RESEARCH §6) and preserves the pre-G-02 behavior.
  const sortedRows =
    sortState.sort && sortState.dir
      ? rows.slice().sort((a, b) => compareRows(a, b, sortState.sort!, sortState.dir!))
      : rows.slice().sort((a, b) => a.userName.localeCompare(b.userName));

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((header) => {
                const isActive = sortState.sort === header.key;
                const ariaSort: 'ascending' | 'descending' | 'none' = isActive
                  ? sortState.dir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none';
                const alignClass = header.align === 'left' ? 'text-left' : 'text-right';
                return (
                  <th
                    key={header.key}
                    aria-sort={ariaSort}
                    // MKS-332: `title` rather than the shared MoneyMetricLabel
                    // — the header text already lives inside a sort <button>,
                    // and nesting a second focusable element in it would be a
                    // worse trade than a native tooltip.
                    title={header.tooltip}
                    className={`px-4 py-3 ${alignClass} text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${
                      header.key === 'userName' ? stickyHead : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => onHeaderClick(header.key)}
                      className={`w-full ${alignClass} text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded`}
                    >
                      {header.label}
                      {isActive && (
                        <span className="ml-1">{sortState.dir === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  </th>
                );
              })}
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
              >
                Timesheets
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedRows.map((row) => {
              const totalNonBill = row.internalHours + row.ptoHours + row.otherHours;
              // D-06 — the margin pair renders with negative-styling: text-red-600
              // when the underlying numeric margin is negative. Inline the literal
              // class strings so the styling is visible at the JSX site, not buried
              // in a helper (auditability + grep-ability).
              const margin1NumClass = row.margin1 < 0 ? 'text-red-600 font-medium' : 'text-gray-900';
              // D-06 dash rendering — when row.marginPct1 === null, the formatter
              // returns the em-dash and the helper assigns the muted-gray class.
              const margin1PctClass = marginPctCellClass(row.margin1, row.marginPct1);

              const isExpanded = expanded.has(row.userId);

              return (
                <Fragment key={row.userId}>
                <tr className="group hover:bg-gray-50">
                  <td className={`px-4 py-2 text-sm text-gray-900 whitespace-nowrap ${stickyBody}`}>
                    {/* Quick task 260602-g6r — hover-trendline anchor.
                         The <span> wraps the username text so the popover can
                         anchor on its DOMRect. tabIndex={0} makes the cell
                         keyboard-focusable; onFocus opens the popover with no
                         delay for a11y. */}
                    <span
                      tabIndex={0}
                      data-testid={`username-cell-${row.userId}`}
                      className="cursor-default focus:outline-none focus:ring-2 focus:ring-blue-200 rounded"
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        onUserNameHoverEnter?.(row.userId, row.userName, rect);
                      }}
                      onMouseLeave={() => onUserNameHoverLeave?.()}
                      onFocus={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        onUserNameFocus?.(row.userId, row.userName, rect);
                      }}
                      onBlur={() => onUserNameBlur?.()}
                    >
                      {row.userName}
                    </span>
                    {/* MKS-479 — this person left; their approved hours are
                         still inside the reported period, so the row stays and
                         says so. Outside the hover anchor above, so the
                         trend-popover keeps anchoring on the name alone. */}
                    {archivedUsersOn && row.isArchived && (
                      <span
                        data-testid={`archived-badge-${row.userId}`}
                        className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
                      >
                        Archived
                      </span>
                    )}
                    {/* MKS-331 — the breakdown that used to cost four columns. One button
                         per person, right next to the name it belongs to. */}
                    <button
                      type="button"
                      aria-expanded={isExpanded}
                      aria-label={`${isExpanded ? 'Hide' : 'Show'} breakdown for ${row.userName}`}
                      onClick={() => toggleExpanded(row.userId)}
                      className="ml-2 text-xs text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded"
                    >
                      {isExpanded ? '▾' : '▸'} detail
                    </button>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700 text-right whitespace-nowrap">
                    {formatHours(row.billableHours)}
                  </td>
                  {/* G-04 — derived total-hours cell (sum of the four hour
                       buckets). Idle users (totalHours === 0) render `0`
                       per locked decision in 57-VERIFICATION.md G-04
                       acceptance bullet 8. formatHours now returns '0' for
                       zero (F-007 fix), so no special-case needed here. */}
                  <td className="px-4 py-2 text-sm text-gray-700 text-right whitespace-nowrap">
                    {formatHours(row.totalHours)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 text-right whitespace-nowrap">
                    {formatMoney(row.cost)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 text-right whitespace-nowrap">
                    {formatMoney(row.billableValue)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 text-right whitespace-nowrap">
                    {formatMoney(row.invoicedAmount)}
                  </td>
                  {/* MKS-332 follow-up — mirrors `applyMoneyVocabulary`. */}
                  <td className="px-4 py-2 text-sm text-gray-900 text-right whitespace-nowrap">
                    {formatMoney(row.paidAmount ?? 0)}
                  </td>
                  <td className={`px-4 py-2 text-sm text-right whitespace-nowrap ${margin1NumClass}`}>
                    {formatMoney(row.margin1)}
                  </td>
                  <td className={`px-4 py-2 text-sm text-right whitespace-nowrap ${margin1PctClass}`}>
                    {formatMarginPct(row.marginPct1)}
                  </td>
                  {/* G-05 — work-delivered cell (engineering commits from
                       Phase 32 — Engineering Metrics). Formatter renders `—`
                       em-dash for zero-commit users (idle) and
                       `{N} commits · +{A}/−{D}` otherwise, with en-US locale
                       thousands separator and U+2212 minus per locked
                       decision in 57-VERIFICATION.md G-05. */}
                  <td className="px-4 py-2 text-sm text-gray-700 text-right whitespace-nowrap">
                    {formatWorkDelivered(row.workDelivered)}
                  </td>
                  {/* Per-user timesheets overlapping the report range — one
                       link per timesheet to the manager timesheet detail page.
                       Idle users (no active timesheet in range) render `—`. */}
                  <td className="px-4 py-2 text-sm text-right whitespace-nowrap">
                    {row.timesheets.length === 0 ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <div className="flex flex-col items-end gap-0.5">
                        {row.timesheets.map((ts) => (
                          <Link
                            key={ts.id}
                            to={`/manager/timesheets/${ts.id}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {ts.periodName}
                          </Link>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
                {/* MKS-331 — the per-person breakdown: the four non-billable hour buckets
                     that lost their columns, plus the three cost figures the report has
                     always carried in its payload and never had room to show. */}
                {isExpanded && (
                  <tr className="bg-gray-50" data-testid={`detail-row-${row.userId}`}>
                    <td colSpan={headers.length + 1} className="px-4 py-3">
                      <dl className="flex flex-wrap gap-x-8 gap-y-1 text-xs text-gray-600">
                        <div>
                          <dt className="inline font-medium">Internal hrs:</dt>{' '}
                          <dd className="inline">{formatHours(row.internalHours)}</dd>
                        </div>
                        <div>
                          <dt className="inline font-medium">PTO hrs:</dt>{' '}
                          <dd className="inline">{formatHours(row.ptoHours)}</dd>
                        </div>
                        <div>
                          <dt className="inline font-medium">Other hrs:</dt>{' '}
                          <dd className="inline">{formatHours(row.otherHours)}</dd>
                        </div>
                        <div>
                          <dt className="inline font-medium">Total non-bill:</dt>{' '}
                          <dd className="inline">{formatHours(totalNonBill)}</dd>
                        </div>
                        <div>
                          <dt className="inline font-medium">User costs:</dt>{' '}
                          <dd className="inline">{formatMoney(row.userCosts)}</dd>
                        </div>
                        <div>
                          <dt className="inline font-medium">Extra costs:</dt>{' '}
                          <dd className="inline">{formatMoney(row.extraCosts)}</dd>
                        </div>
                        <div>
                          <dt className="inline font-medium">Assignment costs:</dt>{' '}
                          <dd className="inline">{formatMoney(row.assignmentCosts)}</dd>
                        </div>
                      </dl>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-blue-50 border-t border-gray-200">
                  <td
                    className={`px-4 py-2 text-sm font-semibold text-gray-900 whitespace-nowrap ${stickyFoot}`}
                  >
                    Total
                  </td>
                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                    {formatHours(totals.billableHours)}
                  </td>
                  {/* G-04 — derived total-hours totals cell. Renders `0`
                       literally when the roll-up is zero (F-007 fix: formatHours
                       now returns '0' for zero, so no special-case needed). */}
                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                    {formatHours(totals.totalHours)}
                  </td>
                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                    {formatMoney(totals.cost)}
                  </td>
                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                    {formatMoney(totals.billableValue)}
                  </td>
                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                    {formatMoney(totals.invoicedAmount)}
                  </td>
                  {/* MKS-332 follow-up — mirrors `applyMoneyVocabulary` and
                      the body row above. */}
                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                    {formatMoney(totals.paidAmount ?? 0)}
                  </td>
                  <td
                    className={`px-4 py-2 text-sm text-right whitespace-nowrap font-semibold ${tMargin1NumClass}`}
                  >
                    {formatMoney(totals.margin1)}
                  </td>
                  <td
                    className={`px-4 py-2 text-sm text-right whitespace-nowrap font-semibold ${tMargin1PctClass}`}
                  >
                    {formatMarginPct(totals.marginPct1)}
                  </td>
                  {/* G-05 — work-delivered totals cell. Sums all per-user
                       workDelivered values. Renders `—` when totals.commitCount
                       is 0 (no engineering activity across the team in this
                       period) per the same formatter rules. */}
                  <td className="px-4 py-2 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                    {formatWorkDelivered(totals.workDelivered)}
                  </td>
                  {/* Timesheets column has no roll-up — empty totals cell. */}
                  <td className="px-4 py-2" />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default function BillabilityReportPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlFrom = searchParams.get('from');
  const urlTo = searchParams.get('to');

  const [filters, setFiltersState] = useState<{ from: string; to: string } | null>(
    urlFrom && urlTo ? { from: urlFrom, to: urlTo } : null
  );

  // Quick task 260602-g6r — hover-trendline popover state. `hovered` holds the
  // user the popover should display + the anchor rect to position against.
  // `openTimer` / `closeTimer` are window.setTimeout handles; clearing them
  // on the opposite event prevents flicker as the cursor moves between the
  // cell and the popover.
  type AnchorRect = ReturnType<HTMLElement['getBoundingClientRect']>;
  const [hovered, setHovered] = useState<{
    userId: number;
    userName: string;
    rect: AnchorRect;
  } | null>(null);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  // F-2702: clear any pending hover timers on unmount so they can't fire
  // against an already-unmounted component.
  useEffect(
    () => () => {
      if (openTimerRef.current !== null) window.clearTimeout(openTimerRef.current);
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);
  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const handleUserNameHoverEnter = useCallback(
    (userId: number, userName: string, rect: ReturnType<HTMLElement['getBoundingClientRect']>) => {
      clearCloseTimer();
      clearOpenTimer();
      openTimerRef.current = window.setTimeout(() => {
        setHovered({ userId, userName, rect });
        openTimerRef.current = null;
      }, 250);
    },
    [clearCloseTimer, clearOpenTimer]
  );
  const handleUserNameHoverLeave = useCallback(() => {
    clearOpenTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setHovered(null);
      closeTimerRef.current = null;
    }, 150);
  }, [clearOpenTimer]);
  const handleUserNameFocus = useCallback(
    (userId: number, userName: string, rect: ReturnType<HTMLElement['getBoundingClientRect']>) => {
      clearCloseTimer();
      clearOpenTimer();
      // Keyboard a11y: open immediately, no delay.
      setHovered({ userId, userName, rect });
    },
    [clearCloseTimer, clearOpenTimer]
  );
  const handleUserNameBlur = useCallback(() => {
    clearOpenTimer();
    setHovered(null);
  }, [clearOpenTimer]);

  const handlePopoverMouseEnter = useCallback(() => {
    clearCloseTimer();
  }, [clearCloseTimer]);
  const handlePopoverMouseLeave = useCallback(() => {
    closeTimerRef.current = window.setTimeout(() => {
      setHovered(null);
      closeTimerRef.current = null;
    }, 150);
  }, []);

  // G-02 — sort state seeded from URL on first render. Stays purely client-side;
  // the hook below is intentionally called with `filters` only, not `sortState`,
  // so a sort click never invalidates the TanStack Query cache.
  const [sortState, setSortState] = useState<SortState>(() => parseSortFromUrl(searchParams));

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === '') next.delete(key);
            else next.set(key, val);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setDateFilters = (next: { from: string; to: string }) => {
    setFiltersState(next);
    updateSearchParams({ from: next.from, to: next.to });
  };

  const handleHeaderClick = useCallback(
    (clickedKey: SortKey) => {
      const next = nextSortState(sortState, clickedKey);
      setSortState(next);
      updateSearchParams({ sort: next.sort ?? null, dir: next.dir ?? null });
    },
    [sortState, updateSearchParams]
  );

  // MKS-331 — row filters, seeded from the URL so a filtered report is a shareable link.
  const [rowFilters, setRowFilters] = useState<BillabilityRowFilters>(() => ({
    query: searchParams.get('q') ?? '',
    employeeType: searchParams.get('etype') ?? '',
    showZeroHours: searchParams.get('zeros') === '1',
  }));

  const updateRowFilters = useCallback(
    (patch: Partial<BillabilityRowFilters>) => {
      setRowFilters((prev) => {
        const next = { ...prev, ...patch };
        updateSearchParams({
          q: next.query || null,
          etype: next.employeeType || null,
          zeros: next.showZeroHours ? '1' : null,
        });
        return next;
      });
    },
    [updateSearchParams]
  );

  const { data, isLoading, error } = useBillabilityReport(filters);

  // MKS-206 (110-REVIEW WR-05). `?? []` because the key is absent from any
  // response cached by an older build.
  const discountedInvoices = data?.discountedInvoices ?? [];

  const allRows = useMemo(() => data?.rows ?? [], [data]);
  const typeOptions = useMemo(() => employeeTypeOptions(allRows), [allRows]);
  const visibleRows = useMemo(
    () => filterBillabilityRows(allRows, rowFilters),
    [allRows, rowFilters]
  );
  // The totals have to describe the rows on screen, or the footer reads as a bug the first
  // time somebody types a name into the search box.
  const visibleTotals = useMemo(() => {
    if (!data) return null;
    return sumBillabilityTotals(visibleRows, data.totals);
  }, [visibleRows, data]);

  // MKS-331 — "who billed the most" is the question this report is opened with, so that is
  // the default order. An explicit `?sort=` in the URL always wins.
  const effectiveSort: SortState =
    sortState.sort && sortState.dir ? sortState : { sort: 'billableHours', dir: 'desc' };

  const hiddenZeroCount = allRows.filter((row) => row.totalHours === 0).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/reports')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          &larr; Back to Reports
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Billability Report</h1>
      </div>

      {/* Filters drop-in */}
      <ReportFilters
        onApply={setDateFilters}
        isLoading={isLoading}
        initialFrom={urlFrom || undefined}
        initialTo={urlTo || undefined}
      />

      {/* MKS-331 — row filters. Client-side over the rows already fetched, so none of these
           costs a request. */}
      {data && !isLoading && (
        <div className="flex flex-wrap items-end gap-4 bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col">
            <label
              htmlFor="billability-person"
              className="text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Person
            </label>
            <input
              id="billability-person"
              type="search"
              value={rowFilters.query}
              onChange={(e) => updateRowFilters({ query: e.target.value })}
              placeholder="Name or email"
              className="mt-1 w-56 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <div className="flex flex-col">
            <label
              htmlFor="billability-employee-type"
              className="text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Employee type
            </label>
            <select
              id="billability-employee-type"
              value={rowFilters.employeeType}
              onChange={(e) => updateRowFilters({ employeeType: e.target.value })}
              className="mt-1 w-48 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">All employee types</option>
              {typeOptions.map((option) => (
                <option key={option} value={option}>
                  {option === NO_EMPLOYEE_TYPE ? 'No employee type' : option}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 pb-1 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={rowFilters.showZeroHours}
              onChange={(e) => updateRowFilters({ showZeroHours: e.target.checked })}
              className="rounded border-gray-300"
            />
            Show people with no hours
          </label>
          <p className="pb-1 text-xs text-gray-500">
            {visibleRows.length} of {allRows.length} people
            {!rowFilters.showZeroHours && hiddenZeroCount > 0
              ? ` · ${hiddenZeroCount} with no hours hidden`
              : ''}
          </p>
        </div>
      )}

      {/* Error branch */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{(error as Error).message}</p>
        </div>
      )}

      {/* Loading branch */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}

      {/* Data branch */}
      {data && visibleTotals && !isLoading && (
        <>
          <BillabilityTable
            rows={visibleRows}
            totals={visibleTotals}
            sortState={effectiveSort}
            onHeaderClick={handleHeaderClick}
            onUserNameHoverEnter={handleUserNameHoverEnter}
            onUserNameHoverLeave={handleUserNameHoverLeave}
            onUserNameFocus={handleUserNameFocus}
            onUserNameBlur={handleUserNameBlur}
          />
          {/* MKS-206 (110-REVIEW WR-05) — "Invoiced amt" above is summed from
              per-entry GROSS billing, so on a period containing a discounted
              invoice it reads higher than what the client was actually billed.
              Before Phase 110 no invoice could carry a discount and the gap was
              theoretical; now it is real, and it used to be documented only in
              a source comment nobody reading this page can see. Naming the
              invoices and the amount makes the two figures reconcilable. This
              does NOT redefine "billable" — that belongs to the billing-terms
              unification. */}
          {discountedInvoices.length > 0 && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              role="note"
            >
              <p>
                <strong>Invoiced amt reads gross.</strong> {discountedInvoices.length} invoice
                {discountedInvoices.length > 1 ? 's' : ''} in this period carr
                {discountedInvoices.length > 1 ? 'y' : 'ies'} a discount totalling{' '}
                {formatMoney(data.totals.invoicedDiscountAmount)} CZK, so the Invoiced amt and
                Margin 2 columns above exceed what was actually invoiced by that much.
              </p>
              <ul className="mt-1 list-inside list-disc">
                {discountedInvoices.map((inv) => (
                  <li key={inv.invoiceId}>
                    {/* Deliberately not a link: `/invoices/:id` is behind
                        FinanceRoute, and this report is open to managers who
                        would land on Unauthorized. */}
                    <span className="font-medium">
                      {inv.invoiceNumber || inv.name || `Invoice #${inv.invoiceId}`}
                    </span>{' '}
                    — gross {formatMoney(inv.preDiscountGross)}, discount{' '}
                    {formatMoney(inv.discountAmount)}, invoiced{' '}
                    {formatMoney(inv.invoicedAmount)} CZK
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* D-05a footnote — idle-user cost behavior */}
          <p className="text-xs text-gray-500 italic">
            Idle users (zero approved hours) show 0 cost — matches Payroll Report.
          </p>
        </>
      )}

      {/* Quick task 260602-g6r — single shared hover-trendline popover.
           Rendered at page level (outside the table's overflow wrapper) so
           it never gets clipped. `isOpen` is gated by `hovered != null` which
           in turn is gated by the 250ms hover delay above. */}
      <UsernameTrendPopover
        userId={hovered?.userId ?? 0}
        userName={hovered?.userName ?? ''}
        isOpen={hovered != null}
        anchorRect={hovered?.rect ?? null}
        onMouseEnter={handlePopoverMouseEnter}
        onMouseLeave={handlePopoverMouseLeave}
      />
    </div>
  );
}
